/**
 * Where the session token lives, and where it deliberately does not.
 *
 * **On the web, nowhere.** The API sets the session as an `httpOnly` cookie
 * precisely so a cross-site script cannot read it. Copying that token into
 * `localStorage` "so the client can send a Bearer header" would hand it back to
 * any XSS the app ever ships — the cookie's whole protection, undone for
 * convenience. So on the web this module stores nothing and
 * `authHeaders()` returns no Authorization header. The browser attaches the
 * cookie to same-origin requests by itself, which is the entire point.
 *
 * **In the desktop shell, `localStorage`.** The page is loaded from
 * `tauri://localhost` and talks to an API on a different origin, so no cookie is
 * attached to anything. The Bearer header is the only mechanism available, which
 * means the token has to be stored somewhere that survives a restart. The
 * storage is per-app and not reachable by a website, so the exposure is not the
 * same as the browser case.
 *
 * The device id is a different kind of value and is stored in both: it is not a
 * credential, it is an identifier, and it needs to be stable because CME's
 * billable unit is the device. A device id that changed on every page load would
 * register a new device on every page load.
 */
import { desktopBoot, isDesktop, platform } from './runtime.js';

const TOKEN_KEY = 'aegis.session.token';
const DEVICE_KEY = 'aegis.device.id';
const REGISTERED_KEY = 'aegis.device.registered';

/** Mirrors localStorage so a read does not touch storage on every request. */
let cachedToken: string | null | undefined;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari in private mode, and any embedding that disables storage, throw on
    // access rather than returning null.
    return null;
  }
}

export function sessionToken(): string | null {
  if (!isDesktop()) return null;
  if (cachedToken !== undefined) return cachedToken;
  cachedToken = storage()?.getItem(TOKEN_KEY) ?? null;
  return cachedToken;
}

export function setSessionToken(token: string | null): void {
  if (!isDesktop()) return;
  cachedToken = token;
  const store = storage();
  if (!store) return;
  if (token) store.setItem(TOKEN_KEY, token);
  else store.removeItem(TOKEN_KEY);
}

/**
 * A stable id for this device.
 *
 * The desktop shell registers one at boot and hands it over; the browser has to
 * mint its own and keep it. Clearing site data therefore reads as a new device,
 * which is correct — it is a different browser profile as far as the entitlement
 * ledger is concerned, and over-counting is the safe direction to be wrong in
 * when the count is a licensing number.
 */
export function deviceId(): string | null {
  // The acknowledged id wins for display and for the request header: it is the
  // row the server actually has, and announcing anything else makes the market
  // data gate refuse. What it is NOT is the value to send at sign-in — see
  // `mintedDeviceId`.
  return registeredDeviceId() ?? mintedDeviceId();
}

/**
 * This browser's own id, before any server ever saw it.
 *
 * Stable for the life of the profile, and deliberately unaffected by whichever
 * account signed in last. That distinction is the whole point.
 *
 * `devices.id` is a global primary key, so two accounts used from one browser
 * present the same id and the server registers the second under a derived one
 * (`AuthService.deviceIdFor`). If sign-in sent the *acknowledged* id instead,
 * the input to that derivation would change every time the accounts alternated
 * — account A signs in, gets a derived id, stores it, account B signs in and
 * derives from THAT, and each switch registers a device neither of them asked
 * for until the cap is gone. Sending the stable id makes the derivation land on
 * the same row every time, in both directions.
 */
export function mintedDeviceId(): string | null {
  const boot = desktopBoot();
  if (boot) return boot.deviceId;

  const store = storage();
  if (!store) return null;

  const existing = store.getItem(DEVICE_KEY);
  if (existing) return existing;

  const minted = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  store.setItem(DEVICE_KEY, minted);
  return minted;
}

/**
 * The device id the *server* has acknowledged, or null.
 *
 * **Registration happens at sign-in, not at page load**, because gaining "the
 * ability to access" is the billable event the exchange counts. Until a signup
 * or login has come back with a device id, the one minted above is a proposal,
 * not a fact — and announcing it on every request makes the market-data gate
 * refuse the request with `device_not_registered`, blanking the chart for a user
 * who has done nothing wrong.
 *
 * This is not hypothetical: sending the minted id unconditionally is exactly
 * what broke mock mode the first time this was wired up, because the demo
 * identity never signs in and so never registers anything.
 */
export function registeredDeviceId(): string | null {
  return storage()?.getItem(REGISTERED_KEY) ?? null;
}

/** Called with `deviceId` from a signup or login response. */
export function confirmDevice(id: string | null): void {
  const store = storage();
  if (!store) return;
  if (id) store.setItem(REGISTERED_KEY, id);
  else store.removeItem(REGISTERED_KEY);
}

/**
 * Headers every request carries.
 *
 * The platform always travels — it is not a claim about entitlement, and the
 * server uses it to label a device it later registers. The device id travels
 * only once it is real.
 */
export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const token = sessionToken();
  if (token) headers.authorization = `Bearer ${token}`;

  const device = registeredDeviceId();
  if (device) headers['x-aegis-device-id'] = device;
  headers['x-aegis-platform'] = platform();

  return headers;
}

/** The device descriptor sent with signup and login. */
export function deviceHint(): {
  id?: string; platform: string; label: string; marketDataCapable: boolean;
} {
  const boot = desktopBoot();
  // The minted id, never the acknowledged one. See `mintedDeviceId`.
  const id = mintedDeviceId();
  return {
    ...(id ? { id } : {}),
    platform: platform(),
    label: boot?.label ?? browserLabel(),
    // Every AEGIS surface can render a chart, so every device counts against
    // the cap. Claiming otherwise to dodge the limit would be misreporting to
    // the exchange, which is the operator's licence at stake, not a UX detail.
    marketDataCapable: true,
  };
}

function browserLabel(): string {
  if (typeof navigator === 'undefined') return 'Browser';
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : /Firefox\//.test(ua) ? 'Firefox'
    : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Linux/.test(ua) ? 'Linux'
    : '';
  return os ? `${browser} on ${os}` : browser;
}
